<template>
  <div class="top-section pt-20 pb-32 pt-86--l pb-28--l">
    <div class="top-section__container container-new">
      <div class="top-section__top-info flx flx--col">
        <h1 class="txt--color-04 txt--36px txt--60px--l txt--weight-700 txt--height-44px txt--height-72px--l mb-0">
          {{ $t('cryptoCards.topSection.title') }}
        </h1>
        <p class="txt--color-03 txt--16px txt--26px--l txt--weight-500 txt--height-20px txt--height-34px--l mb-0">
          {{ $t('cryptoCards.topSection.description') }}
        </p>
      </div>
      <div class="top-section__image-block">
        <img :src="require('@/assets/img/crypto-cards/top-section/top-image.webp')" alt="card image" width="100%">
      </div>
      <div class="top-section__btn-block">
        <div
          v-if="loading"
          class="txt--main"
        >
          <exc-loader />
        </div>
        <div v-else-if="!isOrdered" class="top-section__btn-container flx flx--col">
          <p v-if="btnStates.text" class="txt--color-03 txt--16px txt--height-20px mb-0">
            {{ btnStates.text }}
          </p>
          <timer-block
            v-if="isShownTimer"
            :end-date="new Date('Tue Mar 25 2024 12:26:00 GMT+0200')"
            hide-description
            class="txt--main txt--bold txt--36px txt--52px--l txt--height-24px txt--height-30px--l ml-auto mr-auto ml-0--m mr-0--m"
            @stop="timerStopped = true"
          />
          <button-with-arrow
            v-if="btnStates.btnLink"
            full-width
            size="l"
            :to="btnStates.btnLink"
            class="top-section__btn"
          >
            {{ btnStates.btnText }}
          </button-with-arrow>
          <button-with-arrow
            v-else-if="!btnStates.hiddenBtn"
            full-width
            size="l"
            class="top-section__btn"
            @click="btnStates.btnFn"
          >
            {{ btnStates.btnText }}
          </button-with-arrow>
        </div>
        <div v-else class="top-section__ordered-block p-16 bor--radius-10 bg--color-10 flx flx--center">
          <card-icon />
          <p class="txt--color-04 txt--16px txt--height-18px mb-0">
            {{ $t('cryptoCards.topSection.cardIsOrdered') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { ConfirmationModal } from '@/lib/ConfirmationModal.ts'
import { serverFormMixin } from '@/mixins/serverFormMixin'
import { ExcLoader } from '@/components/common'

export default {
  name: 'TopSection',
  components: {
    ExcLoader,
    ButtonWithArrow: () =>
      import('@/components/welcome/elements/ButtonWithArrow.vue'),
    TimerBlock: () =>
      import(/* webpackMode: "eager" */ '@/components/elements/TimerBlock.vue'),
    CardIcon: () => import('@/assets/icons/crypto-cards/top-section/card.svg?inline'),
  },
  mixins: [serverFormMixin],
  props: {
    loading: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      timerStopped: false,
    }
  },
  computed: {
    ...mapState({
      loggedIn: state => state.auth.loggedIn,
      isOrdered: state => state.cryptoCards.isOrdered,
    }),
    isMobile () {
      return this.$mq?.resize && this.$mq?.below(760)
    },
    isShownTimer () {
      return !this.isOrdered && !this.timerStopped
    },
    btnStates () {
      if (!this.loggedIn) {
        return {
          text: this.$t('cryptoCards.topSection.isNotLoginedState.text'),
          btnText: this.$t('cryptoCards.topSection.isNotLoginedState.btnText'),
          btnLink: this.localePath('/login'),
        }
      }

      if (!this.kycIsVerified) {
        return {
          text: this.$t('cryptoCards.topSection.isNotVerifiedState.text'),
          btnText: this.$t('cryptoCards.topSection.isNotVerifiedState.btnText'),
          btnLink: this.localePath('/user/verification'),
        }
      }

      return {
        hiddenBtn: this.isShownTimer,
        text: '',
        btnText: this.$t('cryptoCards.topSection.isNotOrderedCardState.btnText'),
        btnFn: () => this.onSubmit(),
      }
    },
    ...mapGetters({
      kycIsVerified: 'settings/verification/kycIsVerified',
    }),
  },
  methods: {
    async onSubmit () {
      const confirm = await ConfirmationModal.open({
        title: this.$t('cryptoCards.topSection.confirmationModal.title'),
        text: this.$t('cryptoCards.topSection.confirmationModal.description'),
        confirmButtonText: this.$t('cryptoCards.topSection.confirmationModal.confirmButtonText'),
        confirmationImg: 'buy-crypto-card.svg',
        cryptoCard: true,
      })
      if (!confirm) return

      try {
        const { errors } = await this.$api.createCardApplicationCard()

        if (errors) {
          this.mix_notifyServerErrors(errors, {
            ignoreTitleForKeys: ['amount'],
          })
        } else {
          await this.getCardApplicationStatus()

          await ConfirmationModal.open({
            hideCancelBtn: true,
            title: this.$t('cryptoCards.topSection.successfullOrderModal.title'),
            confirmButtonText: 'OK',
            confirmationImg: 'successfull-order.svg',
          })
        }
      } catch (err) {
        console.error(err)
      }
    },
    ...mapActions({
      getCardApplicationStatus: 'cryptoCards/getCardApplicationStatus',
    }),
  },
}
</script>

<style lang="scss" scoped>
.top-section {

  &__container {
    display: grid;
    grid-template-areas: 'top-info' 'image-block' 'btn-block';
    gap: toRem(32px);

    @include bp(m) {
      grid-template-areas: 'top-info image-block' 'btn-block image-block';
      row-gap: toRem(42px);
      column-gap: toRem(12px);
    }
  }

  &__top-info {
    grid-area: top-info;
    gap: toRem(20px);

    @include bp(m) {
      justify-content: flex-end;
    }
  }

  &__image-block {
    grid-area: image-block;

    img {
      min-width: toRem(343px);
    }
  }

  &__btn-block {
    grid-area: btn-block;
  }

  &__btn-container {
    gap: toRem(32px);

    @include bp(m) {
      flex-direction: column-reverse;
      justify-content: flex-end;
    }
  }

  &__btn {
    @include bp(m) {
      max-width: max-content;
    }
  }

  &__ordered-block {
    gap: toRem(14px);
  }
}
</style>
